import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Copy, Check, X, Table as TableIcon } from 'lucide-react';

/**
 * Message pré-rempli à destination du fournisseur pour demander la modification
 * de la puissance souscrite et de la FTA après une analyse TURPE.
 * - Sélection des PDL (cases à cocher) → 1 message par fournisseur (ou tous en une fois).
 * - Fournisseur / destinataire en texte libre (non connu en amont de l'étude).
 * - Copie en TEXTE brut (tableaux alignés) ou en TABLEAU mis en forme (HTML). Jamais une image.
 */

const SEGMENTS = [
  { key: 'HTA', label: 'Segment HTA — Haute Tension', unit: 'kW', color: '#7c3aed', plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'] },
  { key: 'BTSUP', label: 'Segment BT > 36 kVA', unit: 'kVA', color: '#2563eb', plages: ['HPH', 'HCH', 'HPB', 'HCB'] },
  { key: 'BTINF', label: 'Segment BT ≤ 36 kVA', unit: 'kVA', color: '#059669', plages: ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB', 'HP', 'HC', 'Unique'] },
];
const CANON = ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB', 'HP', 'HC', 'Unique'];
const INTRO = "Madame, Monsieur,\nAprès analyse de mes consommations, je souhaite modifier la puissance souscrite de mon compteur pour l'adapter à mes besoins réels.\nMerci de la modifier avec les informations suivantes :";

function segmentFromFormule(f) {
  if (!f) return null;
  if (f.startsWith('HTA')) return 'HTA';
  if (f.startsWith('BTSUP')) return 'BTSUP';
  if (f.startsWith('BTINF')) return 'BTINF';
  return null;
}

export function buildFournisseurEntries(pdls) {
  return (pdls || []).map((p) => {
    const numero = p?.pdl?.numero || p?.numero || '-';
    const c = p?.calculs || {};
    const formuleActuelle = p?.formuleActuelle || c.formuleActuelle || '';
    const formuleProposee = p?.formuleProposee || c.formuleProposee || formuleActuelle;
    const segment = segmentFromFormule(formuleProposee) || segmentFromFormule(formuleActuelle) || p?.tensionActuelle || 'HTA';
    const proposees = c.puissancesProposees || {};
    const actuelles = c.puissancesActuelles || p?.pdl?.puissancesActuelles || {};
    const souscrite = Number(p?.pdl?.puissanceSouscrite || 0);
    const preconisee = Number(c.puissancePreconisee || 0);
    const segDef = SEGMENTS.find((s) => s.key === segment) || SEGMENTS[0];
    const val = (pl) => {
      const v = proposees?.[pl];
      if (v != null && v !== '') return Math.round(Number(v));
      if (preconisee) return Math.round(preconisee);
      if (souscrite) return Math.round(souscrite);
      return null;
    };
    const vals = {};
    segDef.plages.forEach((pl) => { const x = val(pl); if (x != null) vals[pl] = x; });

    let powerChange = false;
    Object.keys(vals).forEach((pl) => {
      const ap = Math.round(Number(actuelles?.[pl] ?? souscrite) || 0);
      if (vals[pl] && ap && vals[pl] !== ap) powerChange = true;
      if (vals[pl] && !ap) powerChange = true;
    });
    const ftaChange = !!(formuleProposee && formuleProposee !== formuleActuelle);
    return { numero, segment, formuleActuelle, formuleProposee, vals, hasChange: ftaChange || powerChange };
  });
}

function buildSegmentGroups(entries) {
  return SEGMENTS.map((seg) => {
    const rows = entries.filter((e) => e.segment === seg.key);
    if (rows.length === 0) return null;
    let columns;
    if (seg.key === 'BTINF') {
      const present = new Set();
      rows.forEach((r) => Object.keys(r.vals).forEach((k) => present.add(k)));
      columns = CANON.filter((c) => present.has(c));
      if (columns.length === 0) columns = ['Unique'];
    } else {
      columns = seg.plages.filter((pl) => rows.some((r) => r.vals[pl] != null));
      if (columns.length === 0) columns = seg.plages;
    }
    return { ...seg, rows, columns };
  }).filter(Boolean);
}

function buildText(groups, client, siren, fournisseur) {
  const L = [];
  L.push('Objet : Demande de modification de puissance souscrite et de FTA');
  if (fournisseur && fournisseur.trim()) L.push(`À l'attention de : ${fournisseur.trim()}`);
  L.push(`Titulaire : ${client || '-'}${siren ? ` — SIREN ${siren}` : ''}`);
  L.push('');
  L.push(INTRO);
  groups.forEach((seg) => {
    L.push('');
    L.push(`${seg.label} (puissances en ${seg.unit}) :`);
    const cols = ['N° PDL', 'Nouvelle FTA', ...seg.columns];
    const rows = seg.rows.map((r) => [r.numero, r.formuleProposee || '-', ...seg.columns.map((c) => (r.vals[c] != null ? String(r.vals[c]) : '—'))]);
    const widths = cols.map((c, i) => Math.max(c.length, ...rows.map((r) => String(r[i]).length)));
    const fmt = (arr) => arr.map((v, i) => String(v).padEnd(widths[i])).join('   ');
    L.push(fmt(cols));
    L.push(widths.map((w) => '-'.repeat(w)).join('   '));
    rows.forEach((r) => L.push(fmt(r)));
  });
  return L.join('\n');
}

function buildHtml(groups, client, siren, fournisseur) {
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let h = `<div style="font-family:Arial,sans-serif;font-size:14px;color:#1e293b;">`;
  h += `<p style="margin:0 0 4px"><strong>Objet :</strong> Demande de modification de puissance souscrite et de FTA</p>`;
  if (fournisseur && fournisseur.trim()) h += `<p style="margin:0 0 4px"><strong>À l'attention de :</strong> ${esc(fournisseur.trim())}</p>`;
  h += `<p style="margin:0 0 12px"><strong>Titulaire :</strong> ${esc(client || '-')}${siren ? ` — SIREN ${esc(siren)}` : ''}</p>`;
  h += `<p style="margin:0 0 12px;white-space:pre-line">${esc(INTRO)}</p>`;
  groups.forEach((seg) => {
    h += `<p style="margin:14px 0 6px;font-weight:bold;color:#312e81">${esc(seg.label)} <span style="font-weight:normal;color:#64748b">(puissances en ${seg.unit})</span></p>`;
    h += `<table style="border-collapse:collapse;font-size:13px;border:1px solid #cbd5e1">`;
    h += `<tr style="background:#f1f5f9"><th style="border:1px solid #cbd5e1;padding:6px 10px;text-align:left">N° PDL</th><th style="border:1px solid #cbd5e1;padding:6px 10px;text-align:left">Nouvelle FTA</th>`;
    seg.columns.forEach((c) => { h += `<th style="border:1px solid #cbd5e1;padding:6px 10px;text-align:left">${esc(c)}</th>`; });
    h += `</tr>`;
    seg.rows.forEach((r) => {
      h += `<tr><td style="border:1px solid #cbd5e1;padding:6px 10px;font-family:monospace">${esc(r.numero)}</td><td style="border:1px solid #cbd5e1;padding:6px 10px;font-weight:bold;color:#0e7490">${esc(r.formuleProposee || '-')}</td>`;
      seg.columns.forEach((c) => { h += `<td style="border:1px solid #cbd5e1;padding:6px 10px">${r.vals[c] != null ? r.vals[c] : '—'}</td>`; });
      h += `</tr>`;
    });
    h += `</table>`;
  });
  h += `</div>`;
  return h;
}

async function copyRich(html, text) {
  try {
    if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
      const item = new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch (e) { /* repli texte ci-dessous */ }
  await navigator.clipboard.writeText(text);
  return true;
}

export function MessageFournisseurButton({ pdls, client, siren, triggerClassName, triggerLabel }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [fournisseur, setFournisseur] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [entriesSnap, setEntriesSnap] = useState([]);
  const [copied, setCopied] = useState(null); // 'text' | 'rich'

  const openModal = () => {
    const source = typeof pdls === 'function' ? pdls() : pdls;
    const e = buildFournisseurEntries(source);
    const hasAnyChange = e.some((x) => x.hasChange);
    const initShowAll = !hasAnyChange;
    const pool = initShowAll ? e : e.filter((x) => x.hasChange);
    setEntriesSnap(e);
    setShowAll(initShowAll);
    setSelected(new Set(pool.map((x) => x.numero)));
    setFournisseur('');
    setCopied(null);
    setOpen(true);
  };

  const pool = useMemo(() => (showAll ? entriesSnap : entriesSnap.filter((e) => e.hasChange)), [entriesSnap, showAll]);
  const nbChanges = useMemo(() => entriesSnap.filter((e) => e.hasChange).length, [entriesSnap]);
  const groupsPool = useMemo(() => buildSegmentGroups(pool), [pool]);
  const chosen = useMemo(() => pool.filter((e) => selected.has(e.numero)), [pool, selected]);
  const groupsChosen = useMemo(() => buildSegmentGroups(chosen), [chosen]);
  const anySelected = chosen.length > 0;

  const toggleShowAll = (v) => {
    setShowAll(v);
    const np = v ? entriesSnap : entriesSnap.filter((x) => x.hasChange);
    setSelected(new Set(np.map((x) => x.numero)));
  };
  const togglePdl = (numero) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(numero)) n.delete(numero); else n.add(numero);
    return n;
  });
  const toggleAllSel = () => setSelected(anySelected ? new Set() : new Set(pool.map((e) => e.numero)));

  const handleCopy = async (kind) => {
    if (groupsChosen.length === 0) return;
    const text = buildText(groupsChosen, client, siren, fournisseur);
    if (kind === 'rich') await copyRich(buildHtml(groupsChosen, client, siren, fournisseur), text);
    else await navigator.clipboard.writeText(text);
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <button
        onClick={openModal}
        className={triggerClassName || 'flex items-center gap-2 bg-[#312e81] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#3730a3] transition-colors'}
        title="Générer le message de demande au fournisseur"
        data-testid="message-fournisseur-btn"
      >
        <FileText className="w-4 h-4" /> {triggerLabel || 'Message fournisseur'}
      </button>

      {open && createPortal((
        <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 p-4 overflow-y-auto print:hidden" data-testid="message-fournisseur-modal" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5" style={{ background: 'linear-gradient(120deg,#1e1b4b,#312e81 55%,#0e7490)' }}>
              <div>
                <h2 className="text-white text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Message de demande au fournisseur</h2>
                <p className="text-indigo-200 text-xs mt-1">
                  Titulaire : {client || '—'}{siren ? ` · SIREN ${siren}` : ''} · {chosen.length}/{pool.length} PDL sélectionnés
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1" data-testid="message-fournisseur-close">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 max-h-[62vh] overflow-y-auto">
              {/* Fournisseur (texte libre) */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Fournisseur / destinataire (optionnel)</label>
                <input
                  type="text"
                  value={fournisseur}
                  onChange={(e) => setFournisseur(e.target.value)}
                  placeholder="ex. EDF Entreprises, TotalEnergies, Engie…"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#312e81]/40"
                  data-testid="message-fournisseur-input"
                />
              </div>

              <div className="bg-slate-50 border border-slate-200 border-l-4 border-l-[#0e7490] rounded-xl px-5 py-4 text-sm leading-relaxed text-slate-800">
                <p className="font-semibold text-slate-500 text-xs mb-1">Objet : Demande de modification de puissance souscrite et de FTA</p>
                {fournisseur.trim() && <p className="font-semibold text-slate-500 text-xs mb-1">À l'attention de : {fournisseur.trim()}</p>}
                <p className="font-semibold text-slate-500 text-xs mb-3">Titulaire : {client || '—'}{siren ? ` — SIREN ${siren}` : ''}</p>
                <p className="whitespace-pre-line">{INTRO}</p>
              </div>

              {groupsPool.length === 0 ? (
                <div className="mt-5 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="message-fournisseur-empty">
                  Aucun PDL avec changement de FTA ou de puissance. Cochez « Inclure tous les PDL » pour tout afficher.
                </div>
              ) : (
                <>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-500">Cochez les PDL à inclure dans ce message (1 message par fournisseur).</span>
                    <button onClick={toggleAllSel} className="text-xs font-semibold text-[#312e81] hover:underline" data-testid="message-select-all">
                      {anySelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  </div>
                  {groupsPool.map((seg) => (
                    <div className="mt-4" key={seg.key} data-testid={`message-segment-${seg.key}`}>
                      <div className="flex items-center gap-2 text-xs font-bold text-[#312e81] uppercase tracking-wide mb-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: seg.color }}></span>
                        {seg.label}
                        <span className="ml-auto text-[11px] font-medium text-slate-500 normal-case tracking-normal">Puissances en {seg.unit}</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border border-slate-200 rounded-lg text-[12.5px]">
                          <thead>
                            <tr className="bg-slate-100 text-slate-600">
                              <th className="w-8 px-2 py-2"></th>
                              <th className="text-left px-2.5 py-2 font-bold">N° PDL</th>
                              <th className="text-left px-2.5 py-2 font-bold">Nouvelle FTA</th>
                              {seg.columns.map((c) => (<th key={c} className="text-left px-2.5 py-2 font-bold">{c}</th>))}
                            </tr>
                          </thead>
                          <tbody>
                            {seg.rows.map((r) => {
                              const isSel = selected.has(r.numero);
                              return (
                                <tr key={r.numero} className={`border-t border-slate-100 ${isSel ? '' : 'opacity-40'}`}>
                                  <td className="px-2 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={isSel}
                                      onChange={() => togglePdl(r.numero)}
                                      className="w-4 h-4 accent-[#312e81]"
                                      data-testid={`message-pdl-check-${r.numero}`}
                                    />
                                  </td>
                                  <td className="px-2.5 py-2 font-mono text-[11.5px]">{r.numero}</td>
                                  <td className="px-2.5 py-2 font-bold text-[#0e7490]">
                                    {r.formuleProposee || '-'}
                                    {r.formuleActuelle && r.formuleProposee !== r.formuleActuelle && (
                                      <span className="block text-[10.5px] font-medium text-slate-400 line-through">{r.formuleActuelle}</span>
                                    )}
                                  </td>
                                  {seg.columns.map((c) => (
                                    <td key={c} className="px-2.5 py-2 font-bold text-[#312e81]">{r.vals[c] != null ? r.vals[c] : '—'}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="message-toggle-all">
                <input type="checkbox" checked={showAll} onChange={(e) => toggleShowAll(e.target.checked)} className="w-4 h-4 accent-[#312e81]" />
                Inclure tous les PDL (même sans changement)
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleCopy('text')}
                  disabled={chosen.length === 0}
                  className="flex items-center gap-2 bg-white border-2 border-slate-300 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-100 disabled:opacity-40 transition-colors"
                  data-testid="copy-text-btn"
                >
                  {copied === 'text' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied === 'text' ? 'Copié !' : 'Copier en texte'}
                </button>
                <button
                  onClick={() => handleCopy('rich')}
                  disabled={chosen.length === 0}
                  className="flex items-center gap-2 text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 transition-opacity"
                  style={{ background: 'linear-gradient(120deg,#0e7490,#312e81)' }}
                  data-testid="copy-rich-btn"
                >
                  {copied === 'rich' ? <Check className="w-4 h-4" /> : <TableIcon className="w-4 h-4" />}
                  {copied === 'rich' ? 'Copié !' : 'Copier en tableau mis en forme'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
}

export default MessageFournisseurButton;
